/* eslint-disable @typescript-eslint/no-explicit-any */
import { batch, createMemo, createSignal } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { isServer } from "solid-js/web";

import { getUserInfoApi } from "@/api/clientApi/user.ts";
import { getAgentRewardRedDotDataApi } from "@/api/clientApi/userAgent.ts";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { ErrorNumberEnum } from "@/enum/request.ts";
import { useAfterLogin } from "@/hooks/login/useAfterLogin.tsx";
import { useGlobalCacheCookie } from "@/hooks/useGlobalCacheCookies.ts";
import { initUserHeaderPoint } from "@/hooks/wallet/useWallet.tsx";
import { t } from "@/i18n/index.ts";
import { clearToken } from "@/lib/apiUtils.ts";
import { routerUtils } from "@/lib/routerUtils.ts";
import { isEqual } from "@/lib/utils.ts";
import { KYCStore } from "@/stores/kycStore.ts";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { CryptoConfItem } from "@/types/crypto.ts";
import { GetUserInfoRsp, UserPoint } from "@/types/swagger/front.ts";
import { LoginV2Resp } from "@/types/swagger/loginV2.ts";
import { cookieStorage } from "@solid-primitives/storage";

import { LocalStorageKey, POINT_TYPE } from "../constants/constants.ts";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "../lib/localStorage.ts";
import { clearBonusCenterStore } from "./bonusCenterStore.ts";
import { cryptoStore, defaultPointCfg } from "./cryptoStore.ts";
import { makePersisted } from "./persisted/persisted.ts";

type TUserStore = {
  userInfo?: Partial<GetUserInfoRsp>;
  isLogin?: boolean;
  walletPointType: number;
  currentGamePointType?: number;
  pointType: number;
  affiliateRewardTip: boolean;
} & Partial<LoginV2Resp>;

const [store, setUserStore, init] = makePersisted(
  createStore<TUserStore>({
    userInfo: {},
    device_id: "",
    expire_time: 0,
    force_2FA_after_login: false,
    google_status: 0,
    is_reg: false,
    next_to_verify_device: false,
    pack_no: 0, // 未登录渠道
    general: 0, // 未登录总代
    stop_report: false,
    token: getLocalStorageItem(LocalStorageKey.TOKEN, ""),
    user_id: 0,
    verify_device_mode: 0,
    isLogin: false,
    walletPointType: 0, //用户当前选择货币
    currentGamePointType: 0,
    pointType: 0, // 页面顶部用户选中的货币，第一次读默认配置，之后读缓存
    affiliateRewardTip: false, // 联盟计划奖励红点
  }),
  {
    storage: !isServer ? localStorage : undefined,
    name: LocalStorageKey.USER_STORE,
  },
);

const [recentGameList, setRecentGameList] = createSignal<number[]>([]);
const [collectGameList, setCollectGameList] = createSignal<number[]>([]);

export {
  recentGameList,
  setRecentGameList,
  collectGameList,
  setCollectGameList,
};

export const initUserStore = init;
export const userStore = store;
export { store, setUserStore };
// 更新登录后基础用户数据
export const upDateUserStore = function (value: Partial<LoginV2Resp>) {
  const keys = Object.keys(store) as (keyof TUserStore)[];
  const picked: Partial<TUserStore> = {};

  keys.forEach((key) => {
    if (key in value) {
      picked[key] = value[key as keyof LoginV2Resp] as any;
    }
  });
  console.log("upDateUserStore", value);

  setUserStore(
    reconcile({
      ...picked,
      isLogin: !!value.token,
      userInfo: userStore.userInfo,
      walletPointType: POINT_TYPE,
      pointType: POINT_TYPE,
      token: value.token || "",
      affiliateRewardTip: userStore.affiliateRewardTip,
    }),
  );

  if (value.token) {
    setLocalStorageItem(LocalStorageKey.TOKEN, value.token);
    useGlobalCacheCookie().set(LocalStorageKey.IS_LOGIN, "logined");
  }
  if (value.user_id) {
    setLocalStorageItem(LocalStorageKey.USER_ID, value.user_id);
  }
};

export const isLogin = () =>
  !!useGlobalCacheCookie().get(LocalStorageKey.IS_LOGIN);

// 登出
export const clearUserStore = function () {
  setUserStore({
    userInfo: {},
    device_id: "",
    expire_time: 0,
    force_2FA_after_login: false,
    general: 0,
    google_status: 0,
    is_reg: false,
    next_to_verify_device: false,
    pack_no: 0,
    stop_report: false,
    token: "",
    user_id: 0,
    verify_device_mode: 0,
    isLogin: false,
    walletPointType: 0, //用户当前选择货币
    pointType: POINT_TYPE,
    affiliateRewardTip: false,
  });
  removeLocalStorageItem(LocalStorageKey.TOKEN);
  removeLocalStorageItem("AccountCurPointType");
  removeLocalStorageItem(LocalStorageKey.USER_ID);
};

// 更新用户信息
export const upDateUserInfo = function (value: Partial<GetUserInfoRsp>) {
  batch(() => {
    if (!isEqual(userStore.userInfo, value)) {
      setUserStore("userInfo", reconcile({ ...value }));
    }
    if (userStore.walletPointType !== value.default_point_type) {
      setUserStore("walletPointType", value.default_point_type ?? 0);
    }
  });
};

export const getWalletPointType = function () {
  return userStore.walletPointType || getDefaultPointType();
};
export const setWalletPointType = function (pointType: number) {
  setUserStore("walletPointType", pointType);
};
export const setCurrentGamePointType = function (pointType: number) {
  setUserStore("currentGamePointType", pointType);
};
export const setPointType = function (pointType: number) {
  setUserStore("pointType", pointType);
  localStorage.setItem("AccountCurPointType", String(pointType));
};
//默认币种
export const getDefaultPointType = function () {
  if (store.isLogin) {
    return userStore.walletPointType;
  }
  return sysConfigStore.point_type;
};
export const getDefaultPointTypeByName = function () {
  let name: string = "";
  cryptoStore.fiat_currency_config.forEach((item) => {
    if (item.point_type == getDefaultPointType()) {
      name = item.name;
      return;
    }
  });
  return name;
};
export const getRewardPointType = function () {
  if (cryptoStore.plat_coin_point_type) {
    return cryptoStore.plat_coin_point_type;
  } else {
    return getDefaultPointType();
  }
};
// 绑定货币的展示精度
export const getBindPointFixed = function () {
  const pointType = getDefaultPointType();
  const crypto = cryptoStore.crypto_maps.get(pointType);
  if (!crypto) return 0;
  let value = Number(Math.log10(crypto.dipaly_point_ratio)) || 0;
  if (value < 0) {
    value = 0;
  }
  return value;
};

export const getUserPoint = function (pointType: number = 0) {
  let pointVal = 0;
  const curPointType = pointType || userStore.walletPointType;
  const curCryptoUserInfos = userStore.userInfo?.crypto_user_points?.filter(
    (item) => item.pointType == curPointType,
  );
  const curCryptoUserInfo =
    (curCryptoUserInfos &&
      curCryptoUserInfos.length &&
      curCryptoUserInfos[0]) ||
    null;
  if (curCryptoUserInfo?.point) {
    pointVal = curCryptoUserInfo?.point;
  }
  return pointVal;
};

export const getUserPointInfo = function (pointType: number = 0) {
  const curPointType = pointType || userStore.walletPointType;
  const curCryptoUserInfos = userStore.userInfo?.crypto_user_points?.filter(
    (item) => item.pointType === POINT_TYPE,
  );
  const curCryptoUserInfo =
    (curCryptoUserInfos &&
      curCryptoUserInfos.length &&
      curCryptoUserInfos[0]) ||
    null;

  return curCryptoUserInfo;
};

export const updateCryptoUserPoint = function (point: UserPoint) {
  const existingPoints = userStore.userInfo?.crypto_user_points || [];

  // 使用 Map 来存储和更新点数
  const pointsMap = new Map(
    existingPoints.map((existingPoint) => [
      existingPoint.pointType,
      existingPoint,
    ]),
  );

  // 更新单条点数
  pointsMap.set(point.pointType, point);

  // 转换回数组并更新状态
  const updatedPoints = Array.from(pointsMap.values());

  setUserStore(
    "userInfo",
    reconcile({
      ...userStore.userInfo,
      crypto_user_points: updatedPoints,
    }),
  );
};
// 个人资料
const headerNum: number = 15;
type HeaderInfo = {
  headimg: string; //头像
  nickname: string; //昵称
  error: string;
};
const [headerInfo, setHeaderInfo] = createStore<HeaderInfo>({
  headimg: "0",
  nickname: "",
  error: "",
});
const validateHeaderInfo = (filed: string, value: string) => {
  let error: string = "";
  switch (filed) {
    case "nickname":
      if (!value || value.trim()?.length < 1 || value.trim()?.length > 15) {
        error = t("home.nickname_error");
      }
      break;
    default:
      break;
  }
  setHeaderInfo("error", error);
};
export { headerNum, headerInfo, setHeaderInfo, validateHeaderInfo };

// 用户绑定货币 数值信息
export const bindPoint = (): UserPoint | null => {
  return getUserPointInfo(getDefaultPointType()) || null;
};
// 用户绑定货币 货币信息
export const bindPointInfo = (): CryptoConfItem | null => {
  const pointType = getDefaultPointType();
  return cryptoStore.crypto_maps.get(pointType) || defaultPointCfg;
};

const getUserInfoFailedAfterLogin = (err: any) => {
  const { showDialog } = useDialog();
  const content = t(`tops.error_${err._errno}`);
  const confirmText = t("common.confirm");
  const cancelText = t("common.cancel");
  showDialog({
    type: DialogType.Tips,
    data: {
      title: t("common.tips"),
      content: content,
      confirmText: confirmText,
      cancelText: cancelText,
      onConfirm: () => {
        window.location.reload();
      },
    },
  });
};

export const checkLoginCallback = (valid: boolean, err: any = {}) => {
  //已获取到真实登录状态 设置
  if (valid) {
    console.log("checkLoginCallback1", userStore.token);
    initUserHeaderPoint();
    useAfterLogin().afterLoginSuccess();
  } else {
    console.error("checkLoginCallback", userStore.token, err._errno);
    if (
      userStore.token &&
      err._errno != ErrorNumberEnum.TokenError &&
      err._errno != ErrorNumberEnum.TokenErrorDel
    ) {
      getUserInfoFailedAfterLogin(err);
    } else {
      clearToken();
      clearUserStore();
      clearBonusCenterStore();
      routerUtils.push("/login");
    }
  }
};

export const getUserInfo = async (need_auth?: boolean) => {
  const res = await getUserInfoApi();
  if (res) {
    upDateUserInfo(res);
    if (need_auth) {
      KYCStore.getUserKycAuthByAction();
    }
  }
  return res;
};
// 获取用户渠道和总代
export const getUserChannel = () => {
  return userStore.isLogin
    ? {
        general: userStore.userInfo?.general || 0,
        channel: userStore.userInfo?.pack_no || 0,
      }
    : { general: userStore.general || 0, channel: userStore.pack_no || 0 };
};

// 获取联盟计划奖励红点
export const getAgentRewardDotData = async () => {
  const res = await getAgentRewardRedDotDataApi();
  const inUnreadCount = res?.agent_friend_unread_count ?? 0;
  const agentUnreadCount = res?.agent_unread_count ?? 0;
  setUserStore("affiliateRewardTip", inUnreadCount > 0 || agentUnreadCount > 0);
  return res;
};
